import { faPhone } from '@fortawesome/pro-regular-svg-icons/faPhone';
import defaultMasthead from '@propertypal/shared/src/resources/images/agents/generic-masthead.png';
import { AgentDetails, Reason } from '@propertypal/shared/src/types/agents';
import AgentLogo from '@propertypal/web-ui/src/agents/AgentLogo';
import BranchOverview from '@propertypal/web-ui/src/agents/BranchOverview';
import Button from '@propertypal/web-ui/src/buttons/Button';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import AgentAccreditations from '@propertypal/web-ui/src/property/AgentAccreditations';
import PropertyGrid from '@propertypal/web-ui/src/property/PropertyGrid';
import { MidHeading } from '@propertypal/web-ui/src/typography';
import Link from 'next/link';
import React, { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import useCallAgent from '../../utils/useCallAgent';
import {
  AccreditationsWrapper,
  BranchesWrapper,
  CallAgentText,
  Content,
  CoverPhoto,
  FormWrapper,
  Header,
  HeaderRow,
  TextWrapper,
} from './AgentDetails.pn.style';
import { LinkList } from './AgentDetails.style';
import AgentEmailForm from './AgentEmailForm';

interface Props {
  reasons: Reason[];
  agentDetails: AgentDetails;
}

const AgentDetailsBox: FunctionComponent<Props> = (props) => {
  const [branch] = props.agentDetails.branches;
  const theme = useTheme();
  const { phoneNumber, revealAgentNumber } = useCallAgent();
  const emailEnabled = !!props.agentDetails.branches.find((agent) => agent.emailEnabled);
  const showAccountContact = !!props.agentDetails.branches.find((agent) => agent.showAccountContact);
  const backgroundImage = props.agentDetails.heroImageUrl
    ? `url("${props.agentDetails.heroImageUrl}")`
    : `url(${defaultMasthead.src})`;

  return (
    <>
      <CoverPhoto style={{ backgroundColor: branch.logo?.standardBackgroundColour, backgroundImage }} />

      <Header data-testid="agent-details-page">
        <HeaderRow>
          <div>
            <AgentLogo image={branch.logo?.standard} color={branch.logo?.standardBackgroundColour} size={90} />
            <h1>{branch.organisation}</h1>
            {props.agentDetails.branches.length <= 1 && (
              <p style={{ color: theme.backgroundMid }}>
                <b>{branch.displayAddress}</b>
              </p>
            )}

            {branch.websiteUrl && (
              <p style={{ marginTop: 15 }}>
                <a href={branch.websiteUrl}>Visit Website</a>
              </p>
            )}
          </div>

          {props.agentDetails.branches.length <= 1 && (
            <div style={{ textAlign: 'right' }}>
              <CallAgentText>Call {branch.organisation}</CallAgentText>

              {!phoneNumber && (
                <Button
                  containerStyle={{ maxWidth: 260, margin: '10px auto 0' }}
                  icon={faPhone}
                  onClick={() => revealAgentNumber(branch, 'Agent Landing Page')}
                >
                  Call Agent
                </Button>
              )}

              {phoneNumber && (
                <a href={`tel:${phoneNumber}`}>
                  <Button containerStyle={{ maxWidth: 260, margin: '10px auto 0' }} icon={faPhone}>
                    {phoneNumber}
                  </Button>
                </a>
              )}
            </div>
          )}
        </HeaderRow>
      </Header>

      <PageWrapper>
        <Content>
          <div style={{ flex: 1 }}>
            {branch.profile?.summary && (
              <TextWrapper>
                <div dangerouslySetInnerHTML={{ __html: branch.profile.summary }} />
              </TextWrapper>
            )}

            <BranchesWrapper>
              {props.agentDetails.branches.map((agent) => (
                <BranchOverview
                  key={agent.accountNumber}
                  agent={agent}
                  compact={props.agentDetails.branches.length > 1}
                />
              ))}
            </BranchesWrapper>
          </div>

          {emailEnabled && showAccountContact && (
            <FormWrapper>
              <h3>Contact Agent</h3>
              <AgentEmailForm
                agents={props.agentDetails.branches}
                reasons={props.reasons}
                loggedInUser={props.agentDetails.loggedInUser}
                compact
              />
            </FormWrapper>
          )}
        </Content>

        {!!props.agentDetails.accreditations?.length && (
          <AccreditationsWrapper>
            <h3>Accreditations</h3>

            <AgentAccreditations
              accreditations={props.agentDetails.accreditations}
              missingAccreditations={props.agentDetails.missingAccreditations}
              center
            />
          </AccreditationsWrapper>
        )}

        {props.agentDetails.properties && props.agentDetails.properties.length > 0 && (
          <>
            <MidHeading mt={60} mb={30} data-testid="recently-added">
              Recently Added Properties
            </MidHeading>

            <PropertyGrid containerStyle={{ padding: 0 }} properties={props.agentDetails.properties} hideFooter />
          </>
        )}

        {props.agentDetails.stats.category && props.agentDetails.stats.category.length > 0 && (
          <>
            <MidHeading mt={60} mb={30} data-testid="more-properties">
              Browse more properties from {branch.organisation}
            </MidHeading>

            <LinkList>
              {props.agentDetails.stats.category.map((search) => (
                <Link key={search.searchUrl.url} href={search.searchUrl.url}>
                  {search.searchUrl.label}
                </Link>
              ))}
            </LinkList>
          </>
        )}
      </PageWrapper>
    </>
  );
};

export default AgentDetailsBox;
